import { useState } from 'react'
import { Loader2, ScanFace } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'

interface Props {
  size?: 'md' | 'sm'
}

/**
 * One-tap Apple-Center-Stage-style auto-framing toggle. Reads the
 * EFFECTIVE `voiceStore.autoFaceCenter` for the highlight; toggling
 * calls `setAutoFaceCenter`, which saves the user's pref + clears any
 * background auto-suppression and fans the change out to mediaManager
 * (lazy-loads MediaPipe Tasks Vision the first time it's flipped on).
 * Shared between DM call and voice-room surfaces because they ride the
 * same publication.
 */
export default function AutoFaceCenterButton({ size = 'md' }: Props) {
  const enabled = useVoiceStore((s) => s.autoFaceCenter)
  const setAutoFaceCenter = useVoiceStore((s) => s.setAutoFaceCenter)
  const [loading, setLoading] = useState(false)
  const handleClick = async () => {
    if (loading) return
    const next = !enabled
    if (next) {
      setLoading(true)
      try { setAutoFaceCenter(true) } finally {
        // mediaManager.setAutoFaceCenter awaits the model load before
        // calling faceTracker.enable; release the spinner after the
        // store fan-out resolves so the user gets clear feedback
        // during the first ~1s cold-start.
        setTimeout(() => setLoading(false), 200)
      }
    } else {
      setAutoFaceCenter(false)
    }
  }
  const sz = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10'
  const iconSize = size === 'sm' ? 14 : 18
  return (
    <button
      onClick={handleClick}
      disabled={loading}
      title={enabled ? 'Stop auto-framing' : 'Auto-frame on my face (Center Stage)'}
      className={`${sz} rounded-full flex items-center justify-center transition duration-150 ${
        enabled
          ? 'bg-primary/20 text-primary'
          : 'bg-muted text-foreground hover:bg-muted/80'
      } ${loading ? 'opacity-60 cursor-wait' : ''}`}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin" /> : <ScanFace size={iconSize} />}
    </button>
  )
}
